import React from 'react';
import { motion } from 'framer-motion';
import {
  Wrench,
  Zap,
  Settings,
  Archive,
  Shield,
  Sparkles,
  RotateCcw,
  Palette,
  Target,
  Car,
  Trophy,
  Cog,
  Droplets,
  Activity,
  Scan,
  CircuitBoard,
  Battery,
  HardHat
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { FirebaseImage } from '../components/FirebaseImage';

const ServicesPage: React.FC = () => {
  const { t, translations } = useLanguage();

  const standardServices = [
    {
      id: 'sales',
      icon: Car,
      title: t('services.standard.items.sales.title'),
      description: t('services.standard.items.sales.description'),
      details: translations.services?.standard?.items?.sales?.details || [],
      image: '/Vente-pneu.jpg'
    },
    {
      id: 'chains',
      icon: Zap,
      title: t('services.standard.items.chains.title'),
      description: t('services.standard.items.chains.description'),
      details: translations.services?.standard?.items?.chains?.details || [],
      image: '/Chaines-neige.jpg'
    },
    {
      id: 'mounting',
      icon: Wrench,
      title: t('services.standard.items.mounting.title'),
      description: t('services.standard.items.mounting.description'),
      details: translations.services?.standard?.items?.mounting?.details || [],
      image: '/montage-pneu.jpg'
    },
    {
      id: 'geometry',
      icon: Settings,
      title: t('services.standard.items.geometry.title'),
      description: t('services.standard.items.geometry.description'),
      details: translations.services?.standard?.items?.geometry?.details || [],
      image: '/geometrie-pneu.jpg'
    },
    {
      id: 'repair',
      icon: Shield,
      title: t('services.standard.items.repair.title'),
      description: t('services.standard.items.repair.description'),
      details: translations.services?.standard?.items?.repair?.details || [],
      image: '/reparation-pneu.jpg'
    },
    {
      id: 'storage',
      icon: Archive,
      title: t('services.standard.items.storage.title'),
      description: t('services.standard.items.storage.description'),
      details: translations.services?.standard?.items?.storage?.details || [],
      image: '/gardiennage-pneu.jpg'
    },
    {
      id: 'cleaning',
      icon: Sparkles,
      title: t('services.standard.items.cleaning.title'),
      description: t('services.standard.items.cleaning.description'),
      details: translations.services?.standard?.items?.cleaning?.details || [],
      image: '/Nettoyage-roues.jpg'
    },
    {
      id: 'wheelChange',
      icon: RotateCcw,
      title: t('services.standard.items.wheelChange.title'),
      description: t('services.standard.items.wheelChange.description'),
      details: translations.services?.standard?.items?.wheelChange?.details || [],
      image: '/Changement-roues.jpg'
    },
    {
      id: 'straightening',
      icon: Palette,
      title: t('services.standard.items.straightening.title'),
      description: t('services.standard.items.straightening.description'),
      details: translations.services?.standard?.items?.straightening?.details || [],
      image: '/Redressement-Jante.jpg'
    },
    {
      id: 'oilChange',
      icon: Droplets,
      title: t('services.standard.items.oilChange.title'),
      description: t('services.standard.items.oilChange.description'),
      details: translations.services?.standard?.items?.oilChange?.details || [],
      image: '/Vidange-Moteur.jpg'
    },
    {
      id: 'maintenance',
      icon: Activity,
      title: t('services.standard.items.maintenance.title'),
      description: t('services.standard.items.maintenance.description'),
      details: translations.services?.standard?.items?.maintenance?.details || [],
      image: '/Revision-Entretien.jpg'
    },
    {
      id: 'diagnostic',
      icon: Scan,
      title: t('services.standard.items.diagnostic.title'),
      description: t('services.standard.items.diagnostic.description'),
      details: translations.services?.standard?.items?.diagnostic?.details || [],
      image: '/Diagnostic-electronique.jpg'
    },
    {
      id: 'brakes',
      icon: CircuitBoard,
      title: t('services.standard.items.brakes.title'),
      description: t('services.standard.items.brakes.description'),
      details: translations.services?.standard?.items?.brakes?.details || [],
      image: '/Systeme-freinage.jpg'
    },
    {
      id: 'battery',
      icon: Battery,
      title: t('services.standard.items.battery.title'),
      description: t('services.standard.items.battery.description'),
      details: translations.services?.standard?.items?.battery?.details || [],
      image: '/Batterie.jpg'
    },
    {
      id: 'carrosserie',
      icon: HardHat,
      title: t('services.standard.items.carrosserie.title'),
      description: t('services.standard.items.carrosserie.description'),
      details: translations.services?.standard?.items?.carrosserie?.details || [],
      image: '/carroserie.jpg'
    }
  ];

  const premiumServices = [
    {
      id: 'trackPrep',
      icon: Target,
      title: t('services.premium.items.trackPrep.title'),
      description: t('services.premium.items.trackPrep.description'),
      details: translations.services?.premium?.items?.trackPrep?.details || []
    },
    {
      id: 'restoration',
      icon: Trophy,
      title: t('services.premium.items.restoration.title'),
      description: t('services.premium.items.restoration.description'),
      details: translations.services?.premium?.items?.restoration?.details || []
    },
    {
      id: 'luxury',
      icon: Car,
      title: t('services.premium.items.luxury.title'),
      description: t('services.premium.items.luxury.description'),
      details: translations.services?.premium?.items?.luxury?.details || []
    },
    {
      id: 'custom',
      icon: Cog,
      title: t('services.premium.items.custom.title'),
      description: t('services.premium.items.custom.description'),
      details: translations.services?.premium?.items?.custom?.details || []
    }
  ];

  const processSteps = [
    {
      id: 'brief',
      number: '01',
      title: t('services.process.steps.brief.title'),
      description: t('services.process.steps.brief.description')
    },
    {
      id: 'execution',
      number: '02',
      title: t('services.process.steps.execution.title'),
      description: t('services.process.steps.execution.description')
    },
    {
      id: 'quality',
      number: '03',
      title: t('services.process.steps.quality.title'),
      description: t('services.process.steps.quality.description')
    }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              {t('services.title')}
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto">
              {t('services.subtitle')}
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-500 mx-auto mt-8 rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Standard Services Section */}
      <section id="standard" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('services.standard.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('services.standard.description')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {standardServices.map((service, index) => {
              const IconComponent = service.icon;
              
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border border-gray-100 hover:border-red-200 group"
                >
                  {/* Image Section */}
                  <div className="relative overflow-hidden rounded-xl mb-6 h-48 bg-gray-100">
                    <FirebaseImage
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-600/5 to-red-600/0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  </div>

                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="w-16 h-16 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:shadow-lg transition-shadow duration-300"
                  >
                    <IconComponent className="w-8 h-8 text-white" />
                  </motion.div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-red-600 transition-colors duration-300">
                    {service.title}
                  </h3>

                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  <ul className="space-y-2">
                    {service.details.map((detail, detailIndex) => (
                      <motion.li
                        key={detailIndex}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 + detailIndex * 0.1 }}
                        className="flex items-center space-x-3 text-gray-600"
                      >
                        <div className="w-2 h-2 bg-red-600 rounded-full flex-shrink-0" />
                        <span>{detail}</span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Premium Services Section */}
      <section id="premium" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('services.premium.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('services.premium.description')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {premiumServices.map((service, index) => {
              const IconComponent = service.icon;
              
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="bg-gradient-to-br from-black to-gray-900 rounded-2xl p-8 text-white shadow-2xl hover:shadow-3xl transition-all duration-300 group"
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: -5 }}
                    className="w-20 h-20 bg-gradient-to-r from-red-600 to-red-500 rounded-2xl flex items-center justify-center mb-6 group-hover:shadow-lg transition-shadow duration-300"
                  >
                    <IconComponent className="w-10 h-10 text-white" />
                  </motion.div>

                  <h3 className="text-3xl font-bold mb-4 group-hover:text-red-400 transition-colors duration-300">
                    {service.title}
                  </h3>

                  <p className="text-gray-300 mb-6 leading-relaxed text-lg">
                    {service.description}
                  </p>

                  <ul className="space-y-3">
                    {service.details.map((detail, detailIndex) => (
                      <motion.li
                        key={detailIndex}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.2 + detailIndex * 0.1 }}
                        className="flex items-center space-x-3 text-gray-200"
                      >
                        <Zap className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <span>{detail}</span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Timeline Section */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {t('services.process.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-500 mx-auto rounded-full" />
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-red-600 via-red-500 to-red-600 rounded-full" />

            <div className="space-y-16">
              {processSteps.map((step, index) => (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.3 }}
                  className={`relative flex items-center ${
                    index % 2 === 0 ? 'justify-start' : 'justify-end'
                  }`}
                >
                  {/* Timeline Node */}
                  <motion.div
                    whileHover={{ scale: 1.2 }}
                    className="absolute left-1/2 transform -translate-x-1/2 w-16 h-16 bg-gradient-to-r from-red-600 to-red-500 rounded-full flex items-center justify-center shadow-2xl z-10"
                  >
                    <span className="text-white font-bold text-lg">{step.number}</span>
                  </motion.div>

                  {/* Content Card */}
                  <motion.div
                    whileHover={{ scale: 1.02, y: -5 }}
                    className={`w-5/12 p-8 bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-2xl ${
                      index % 2 === 0 ? 'mr-auto' : 'ml-auto'
                    }`}
                  >
                    <h3 className="text-2xl font-bold mb-4 text-red-400">
                      {step.title}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {step.description}
                    </p>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mt-20"
          >
            <motion.a
              href="/custom-request"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <span>{t('common.cta.briefProject')}</span>
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                →
              </motion.div>
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;